/**
 * What a person's page does, for the computer's page and the Season pages alike: load them, open their conversation,
 * the actions behind the one menu, saving a tag or a note, and how their story is laid out.
 */
import { useEffect, useState } from "react";
import type { JourneyItem, PersonDetailResponse, PinTarget } from "../../../shared/types";
import type { IconName } from "../../components/ui/Icon";
import type { MenuItem } from "../../components/ui/Menu";
import { normalizeTag } from "../../components/ui/TagPicker";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { useShownAgain } from "../../lib/inApp";
import { lastLoaded, load } from "../../lib/kept";
import { dayHeading } from "../../lib/when";
import { useOpenDirect } from "../../talk/handoff";
import { downloadCsv, peopleCsv } from "./csv";

const DOT: Record<string, { icon: IconName; tone?: string }> = {
  register: { icon: "userplus", tone: "brass" },
  visit: { icon: "globe" },
  form: { icon: "pencil" },
  link: { icon: "link" },
  join: { icon: "door", tone: "wine" },
  watch: { icon: "eye" },
  offer: { icon: "pointer", tone: "brass" },
  convert: { icon: "check", tone: "good" },
  handoff: { icon: "zap" },
  question: { icon: "question", tone: "wine" },
  replied: { icon: "mail", tone: "wine" },
  answer: { icon: "chat" },
  email: { icon: "mail" },
  emailopen: { icon: "mail" },
  reminder: { icon: "clock" },
};

export function dotOf(e: JourneyItem): { icon: IconName; tone?: string } {
  if (e.type === "watch" && (e.pct ?? 0) >= 100) return { icon: "flag", tone: "good" };
  return DOT[e.type] ?? { icon: "info" };
}

export const PIN_WHAT: Record<PinTarget, string> = { message: "A message", post: "An update", comment: "A reply", handout: "A handout" };

/** The story newest first, a heading over each day. */
export function byDay(items: readonly JourneyItem[]): Array<[string, JourneyItem[]]> {
  const out: Array<[string, JourneyItem[]]> = [];
  for (const e of items.toReversed()) {
    const day = dayHeading(e.at);
    const last = out.at(-1);
    if (last && last[0] === day) last[1].push(e);
    else out.push([day, [e]]);
  }
  return out;
}

export function usePerson(id: string) {
  const openDirect = useOpenDirect();
  const toast = useToast();
  const shownAgain = useShownAgain();
  const key = `person:${id}`;
  const [d, setD] = useState<PersonDetailResponse | null>(() => lastLoaded(key));
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setD(lastLoaded(key));
    load(key, () => api.people.get(id))
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [key, id, nonce, shownAgain]);

  const openThread = async () => {
    try {
      await openDirect(id);
    } catch {
      toast({ text: "Their conversation didn't open. Try again.", icon: "info" });
    }
  };

  return { d, failed, reload: () => setNonce((n) => n + 1), openThread };
}

/** The one "..." menu on a person: automation, their own page, export, delete. */
export function usePersonMenu(d: PersonDetailResponse | null, on: { automation: () => void; remove: () => void }): Array<MenuItem | "sep"> {
  const toast = useToast();
  if (!d) return [];
  const p = d.registrant;
  return [
    { label: "Add to automation", icon: "zap", onSelect: on.automation },
    { label: "View their page", icon: "door", onSelect: () => window.open(`/watch/${d.watch_token}`, "_blank", "noopener") },
    {
      label: "Export",
      icon: "download",
      onSelect: () => {
        downloadCsv(peopleCsv([p]), p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
        toast({ text: `Exporting ${p.name}`, icon: "download" });
      },
    },
    "sep",
    { label: "Delete this person", icon: "trash", danger: true, onSelect: on.remove },
  ];
}

/** Saves a tag on a person; true when it saved, so the field can close. */
export function useAddTag(id: string, onAdded: () => void): (value: string) => Promise<boolean> {
  const toast = useToast();
  return async (value) => {
    const tag = normalizeTag(value);
    if (!tag) {
      toast({ text: "Type the tag first.", icon: "info" });
      return false;
    }
    try {
      await api.people.tag(id, { add: [tag] });
      toast({ text: `Tag added: ${tag}`, icon: "tag" });
      onAdded();
      return true;
    } catch {
      toast({ text: "That tag didn't save. Try again.", icon: "info" });
      return false;
    }
  };
}

/** Saves a note on a person; true when it saved, so the box can clear. */
export function useAddNote(id: string, onSaved: () => void): (text: string) => Promise<boolean> {
  const toast = useToast();
  return async (text) => {
    const v = text.trim();
    if (!v) return false;
    try {
      await api.people.addNote(id, { body: v });
      toast("Note saved");
      onSaved();
      return true;
    } catch {
      toast({ text: "That note didn't save. Try again.", icon: "info" });
      return false;
    }
  };
}

/** The tags on their own and the ones the app gave them, each once. */
export const systemTagsOf = (d: PersonDetailResponse): string[] => [...new Set(d.webinars.flatMap((g) => g.tags))];
